// Guardian credential challenge dialog.
// Shown on child-assigned devices when the parent needs to access Settings.
// Lets the guardian authenticate with either their password or PIN without
// creating a full session -- returns a short-lived settings-only token.

import {
  type AccountListEntry,
  type GuardianChallengeResult,
  accountsList,
  guardianChallenge,
} from '@chorepop/core/client';
import { type Component, For, Show, createResource, createSignal } from 'solid-js';

type Props = {
  onSuccess: (settingsToken: string, guardianAccountId: number) => void;
  onCancel: () => void;
  /** When set, verification is locked to the already signed-in guardian. */
  guardianAccountId?: number;
};

type Mode = 'password' | 'pin';

export const GuardianChallengeDialog: Component<Props> = (props) => {
  // Load all guardian accounts from the local DB (they're in the snapshot).
  const [guardians] = createResource(async () => {
    const r = await accountsList();
    if (!r.ok) return [];
    return r.value.filter(
      (a) =>
        a.roleCode === 'guardian' &&
        (props.guardianAccountId === undefined || a.accountId === props.guardianAccountId),
    );
  });

  const [selectedId, setSelectedId] = createSignal<number | null>(props.guardianAccountId ?? null);
  const [mode, setMode] = createSignal<Mode>('password');
  const [password, setPassword] = createSignal('');
  const [pin, setPin] = createSignal('');
  const [busy, setBusy] = createSignal(false);
  const [errorMsg, setErrorMsg] = createSignal<string | null>(null);
  const [lockedUntil, setLockedUntil] = createSignal<string | null>(null);

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    const acctId = selectedId();
    if (!acctId) {
      setErrorMsg('Please select your account.');
      return;
    }
    setBusy(true);
    setErrorMsg(null);

    const credential =
      mode() === 'password'
        ? { kind: 'password' as const, password: password() }
        : { kind: 'pin' as const, pin: pin() };

    const r = await guardianChallenge({ accountId: acctId, credential });
    setBusy(false);

    if (!r.ok) {
      setErrorMsg(r.error.message);
      return;
    }

    const result: GuardianChallengeResult = r.value;
    if (result.outcome === 'success') {
      props.onSuccess(result.settingsToken, acctId);
    } else if (result.outcome === 'failed') {
      const remaining = result.attemptsRemaining;
      setErrorMsg(
        remaining > 0
          ? `Incorrect. ${remaining} attempt${remaining === 1 ? '' : 's'} remaining.`
          : 'Incorrect.',
      );
    } else if (result.outcome === 'locked') {
      setLockedUntil(result.expiresAtUtc);
      setErrorMsg('Too many failed attempts. Please wait before trying again.');
    } else {
      setErrorMsg('That account does not have guardian access.');
    }
  };

  return (
    <div
      class="guardian-challenge-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Parent verification required"
    >
      <div class="guardian-challenge-dialog">
        <h2 class="guardian-challenge-title">Parent verification</h2>
        <p class="guardian-challenge-intro">Enter your credentials to access device settings.</p>

        <Show when={errorMsg()}>
          <div class="form-error" role="alert">
            {errorMsg()}
          </div>
        </Show>

        <form onSubmit={handleSubmit} class="guardian-challenge-form">
          <div class="field-row">
            <label for="guardian-select">Your account</label>
            <Show when={guardians.loading}>
              <p>Loading...</p>
            </Show>
            <Show when={!guardians.loading}>
              <select
                id="guardian-select"
                value={selectedId() ?? ''}
                disabled={props.guardianAccountId !== undefined}
                onChange={(e) => setSelectedId(Number(e.currentTarget.value) || null)}
              >
                <option value="">-- select your account --</option>
                <For each={guardians() ?? []}>
                  {(g: AccountListEntry) => <option value={g.accountId}>{g.displayName}</option>}
                </For>
              </select>
            </Show>
          </div>

          <div class="guardian-challenge-mode-toggle">
            <button
              type="button"
              class={mode() === 'password' ? 'primary' : 'ghost'}
              onClick={() => {
                setMode('password');
                setErrorMsg(null);
              }}
            >
              Password
            </button>
            <button
              type="button"
              class={mode() === 'pin' ? 'primary' : 'ghost'}
              onClick={() => {
                setMode('pin');
                setErrorMsg(null);
              }}
            >
              PIN
            </button>
          </div>

          <Show when={mode() === 'password'}>
            <div class="field-row">
              <label for="guardian-password">Password</label>
              <input
                id="guardian-password"
                type="password"
                autocomplete="current-password"
                value={password()}
                onInput={(e) => setPassword(e.currentTarget.value)}
                disabled={busy() || !!lockedUntil()}
              />
            </div>
          </Show>

          <Show when={mode() === 'pin'}>
            <div class="field-row">
              <label for="guardian-pin">PIN</label>
              <input
                id="guardian-pin"
                type="password"
                inputmode="numeric"
                maxlength="6"
                autocomplete="off"
                value={pin()}
                onInput={(e) => setPin(e.currentTarget.value)}
                disabled={busy() || !!lockedUntil()}
              />
            </div>
          </Show>

          <div class="guardian-challenge-footer">
            <button type="submit" class="primary" disabled={busy() || !!lockedUntil()}>
              {busy() ? 'Verifying...' : 'Continue'}
            </button>
            <button type="button" class="ghost" onClick={props.onCancel}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
